export interface BuildControlClassNameOptions {
  hasError?: boolean;
  isDisabled?: boolean;
  hasValue?: boolean;
  isMultiline?: boolean;
  className?: string;
}

const disabledInputClassName = 'disabled:border-dust! disabled:bg-[#e8eaf0]! disabled:text-ash! disabled:opacity-100';

export function buildInputClassName({
  hasError = false,
  isDisabled = false,
  hasValue = false,
  isMultiline = false,
  className,
}: BuildControlClassNameOptions) {
  const borderClassName = hasError ? 'border-red-500!' : hasValue ? 'border-ash!' : 'border-dust!';
  const heightClassName = isMultiline ? 'min-h-[120px]' : 'h-12!';

  return [
    'w-full rounded-lg bg-white! text-sm text-ash shadow-none!',
    borderClassName,
    isDisabled ? disabledInputClassName : '',
    heightClassName,
    'placeholder:font-normal placeholder:text-dust',
    // plain Input
    '[&.ant-input]:px-3.5! [&.ant-input]:py-0! [&.ant-input]:leading-12! [&.ant-input]:font-medium',
    // Input with prefix/suffix (affix wrapper receives className)
    '[&.ant-input-affix-wrapper]:px-3.5! [&.ant-input-affix-wrapper]:py-0!',
    '[&.ant-input-affix-wrapper]:flex! [&.ant-input-affix-wrapper]:items-center!',
    '[&.ant-input-affix-wrapper]:bg-white! [&.ant-input-affix-wrapper]:shadow-none!',
    '[&_.ant-input]:h-full! [&_.ant-input]:px-0! [&_.ant-input]:py-0!',
    '[&_.ant-input]:leading-12! [&_.ant-input]:font-medium [&_.ant-input]:bg-transparent!',
    '[&_.ant-input]:placeholder:font-normal [&_.ant-input]:placeholder:text-dust',
    '[&.ant-input-affix-wrapper.ant-input-outlined:focus-within]:border-grass!',
    '[&.ant-input-affix-wrapper.ant-input-outlined:focus-within]:shadow-none!',
    '[&.ant-input-outlined:focus]:border-grass! [&.ant-input-outlined:focus]:shadow-none!',
    '[&:hover:not(:disabled):not(.ant-input-affix-wrapper-disabled)]:border-ash!',
    '[&.ant-input-textarea]:rounded-lg [&_.ant-input]:min-h-[120px]',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export function buildSelectClassName({
  hasError = false,
  isDisabled = false,
  hasValue = false,
  className,
}: BuildControlClassNameOptions) {
  const borderClassName = hasError
    ? '[&_.ant-select-selector]:border-red-500!'
    : hasValue
    ? '[&_.ant-select-selector]:border-ash!'
    : '[&_.ant-select-selector]:border-dust!';

  return [
    'w-full h-12! text-sm text-ash',
    '[&_.ant-select-selector]:h-full! [&_.ant-select-selector]:rounded-lg! [&_.ant-select-selector]:px-3.5!',
    '[&_.ant-select-selector]:flex! [&_.ant-select-selector]:items-center! [&_.ant-select-selector]:bg-white!',
    '[&_.ant-select-selector]:shadow-none!',
    borderClassName,
    '[&_.ant-select-selection-item]:flex! [&_.ant-select-selection-item]:h-full! [&_.ant-select-selection-item]:items-center!',
    '[&_.ant-select-selection-item]:font-medium! [&_.ant-select-selection-item]:leading-normal! [&_.ant-select-selection-item]:text-ash!',
    '[&_.ant-select-selection-placeholder]:flex! [&_.ant-select-selection-placeholder]:h-full!',
    '[&_.ant-select-selection-placeholder]:items-center! [&_.ant-select-selection-placeholder]:font-normal!',
    '[&_.ant-select-selection-placeholder]:leading-normal! [&_.ant-select-selection-placeholder]:text-dust!',
    '[&_.ant-select-selection-placeholder]:opacity-100!',
    '[&>.ant-select-arrow]:inset-y-0! [&>.ant-select-arrow]:mt-0! [&>.ant-select-arrow]:flex!',
    '[&>.ant-select-arrow]:h-auto! [&>.ant-select-arrow]:items-center! [&>.ant-select-arrow]:justify-center!',
    '[&.ant-select-focused_.ant-select-selector]:border-grass! [&.ant-select-focused_.ant-select-selector]:shadow-none!',
    '[&.ant-select-open_.ant-select-selector]:border-grass!',
    '[&:hover:not(.ant-select-disabled)_.ant-select-selector]:border-ash!',
    isDisabled
      ? '[&.ant-select-disabled]:opacity-100! [&.ant-select-disabled_.ant-select-selector]:bg-[#e8eaf0]! [&.ant-select-disabled_.ant-select-selector]:border-dust!'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export function buildInputNumberClassName({
  hasError = false,
  isDisabled = false,
  hasValue = false,
  className,
}: BuildControlClassNameOptions) {
  const borderClassName = hasError ? 'border-red-500!' : hasValue ? 'border-ash!' : 'border-dust!';

  return [
    'w-full h-12! rounded-lg bg-white! text-sm text-ash',
    borderClassName,
    '[&.ant-input-number]:px-3.5! [&.ant-input-number]:bg-white! [&.ant-input-number]:shadow-none!',
    '[&_.ant-input-number-input]:h-12! [&_.ant-input-number-input]:font-medium [&_.ant-input-number-input]:text-ash',
    '[&_.ant-input-number-input]:bg-transparent! [&_.ant-input-number-input]:leading-12!',
    '[&_.ant-input-number-input]:placeholder:font-normal [&_.ant-input-number-input]:placeholder:text-dust',
    '[&.ant-input-number-focused]:border-grass! [&.ant-input-number-focused]:shadow-none!',
    '[&.ant-input-number-outlined:hover]:border-ash!',
    '[&.ant-input-number-affix-wrapper]:h-12! [&_.ant-input-number-prefix]:mr-1',
    '[&.ant-input-number-affix-wrapper]:px-3.5! [&.ant-input-number-affix-wrapper]:bg-white!',
    '[&.ant-input-number-affix-wrapper]:flex! [&.ant-input-number-affix-wrapper]:items-center!',
    '[&.ant-input-number-affix-wrapper]:shadow-none!',
    '[&.ant-input-number-affix-wrapper.ant-input-number-focused]:border-grass!',
    '[&.ant-input-number-affix-wrapper.ant-input-number-focused]:shadow-none!',
    isDisabled
      ? '[&.ant-input-number-disabled]:bg-[#e8eaf0]! [&.ant-input-number-disabled]:border-dust! [&.ant-input-number-disabled]:opacity-100 [&.ant-input-number-affix-wrapper-disabled]:bg-[#e8eaf0]!'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export const inputHelperClassName = 'mb-0 mt-1 text-sm text-red-500';
export const inputLabelClassName = 'mb-1 block text-sm font-medium text-ash';
export const inputAdornmentClassName =
  'font-satoshi-medium inline-flex items-center whitespace-nowrap font-medium text-ash';

export const selectPopupClassName = [
  'rounded-lg! border! border-solid! border-dust! bg-white! p-0! shadow-md!',
  '[&_.ant-select-item-group]:sticky [&_.ant-select-item-group]:top-0',
  '[&_.ant-select-item-group]:border-b [&_.ant-select-item-group]:border-solid [&_.ant-select-item-group]:border-fog',
  '[&_.ant-select-item-group]:bg-white [&_.ant-select-item-group]:px-[1.2rem] [&_.ant-select-item-group]:py-4',
  '[&_.ant-select-item-group]:font-medium [&_.ant-select-item-group]:text-ash',
  '[&_.ant-select-item-option]:px-3.5 [&_.ant-select-item-option]:py-3.5 [&_.ant-select-item-option]:text-base',
  '[&_.ant-select-item-option-content]:text-ash',
  '[&_.ant-select-item-option-active]:bg-fog!',
  '[&_.ant-select-item-option-selected]:bg-[#e8f6ef]! [&_.ant-select-item-option-selected]:font-medium!',
].join(' ');
